import { useEffect, useRef, useState } from "react";
import { getLocalCalendarDate, syncUntouchedDateDefault } from "../lib/date.js";
import { currency, toOption } from "../lib/format.js";
import { CustomerLink, displayCustomerName, EmployeeLink } from "../components/recordLinks.jsx";
import { AsyncActionButton, Panel, ResourceTable, SelectField, SimpleForm, TextField, Topbar } from "../components/ui.jsx";

export function OperationsView({
  businessDate,
  resources,
  customers,
  users,
  user,
  onCreate,
  onApproveExpense,
  openCustomer,
  openEmployee,
  openMessage,
  onAddEmployee,
  workflowDraft,
  onWorkflowDraftHandled,
}) {
  const reps = users.filter((item) => item.role === "sales" && item.active !== false);
  const hasActiveSalesReps = reps.length > 0;
  const repOptions = hasActiveSalesReps ? reps.map(toOption) : [{ value: "", label: "No active sales reps available" }];
  const hasOutstandingRecords = resources.outstanding.length > 0;
  const payableOutstanding = resources.outstanding.filter((item) => Number(item.outstandingAmount || 0) > 0);
  const hasPayableOutstanding = payableOutstanding.length > 0;
  const outstandingOptions = hasPayableOutstanding
    ? payableOutstanding.map((item) => ({ value: item.id, label: `${displayCustomerName(customers, item.customerId, item.customerName)} - ${currency(item.outstandingAmount)}` }))
    : [{ value: "", label: "No payable outstanding balances available" }];
  const canAssignTargets = user.role === "manager" || user.role === "admin";
  const canApproveExpenses = user.role === "manager" || user.role === "admin";
  const defaultExpenseDate = businessDate || getLocalCalendarDate();
  const previousDefaultExpenseDate = useRef(defaultExpenseDate);
  const targetFormRef = useRef(null);
  const expenseFormRef = useRef(null);
  const paymentFormRef = useRef(null);
  const [targetForm, setTargetForm] = useState({ userId: "", productCategory: "All", period: "Monthly", revenueTarget: 0, quantityTarget: 0 });
  const [expenseForm, setExpenseForm] = useState({ userId: "", date: defaultExpenseDate, purpose: "", vehicleType: "Bike", distanceKm: 0, ratePerKm: 8 });
  const firstOutstanding = payableOutstanding[0]?.id ?? "";
  const [paymentForm, setPaymentForm] = useState({ outstandingId: firstOutstanding, amount: 10000 });
  const outstandingIds = payableOutstanding.map((item) => `${item.id}:${item.outstandingAmount}`).join("|");
  const paymentOutstanding = payableOutstanding.find((item) => item.id === paymentForm.outstandingId);
  const paymentAmount = Number(paymentForm.amount);
  const paymentBalance = Number(paymentOutstanding?.outstandingAmount || 0);
  const paymentExceedsBalance = hasPayableOutstanding && paymentAmount > paymentBalance;
  const canPostPayment = hasPayableOutstanding && paymentAmount > 0 && !paymentExceedsBalance;

  useEffect(() => {
    const fallbackRepId = reps[0]?.id || "";
    setTargetForm((current) => (reps.some((rep) => rep.id === current.userId) ? current : { ...current, userId: fallbackRepId }));
    setExpenseForm((current) => (reps.some((rep) => rep.id === current.userId) ? current : { ...current, userId: fallbackRepId }));
  }, [reps.map((rep) => rep.id).join("|")]);

  useEffect(() => {
    const previousDefaultDate = previousDefaultExpenseDate.current;
    setExpenseForm((current) => ({
      ...current,
      date: syncUntouchedDateDefault(current.date, previousDefaultDate, defaultExpenseDate),
    }));
    previousDefaultExpenseDate.current = defaultExpenseDate;
  }, [defaultExpenseDate]);

  useEffect(() => {
    if (!workflowDraft || !["target", "expense"].includes(workflowDraft.type)) return;
    const repExists = reps.some((rep) => rep.id === workflowDraft.userId);
    if (!repExists) {
      onWorkflowDraftHandled?.(workflowDraft.id);
      return;
    }
    if (workflowDraft.type === "target" && canAssignTargets) {
      setTargetForm({
        userId: workflowDraft.userId,
        productCategory: workflowDraft.productCategory || "All",
        period: workflowDraft.period || "Monthly",
        revenueTarget: Number(workflowDraft.revenueTarget || 0),
        quantityTarget: Number(workflowDraft.quantityTarget || 0),
      });
      focusForm(targetFormRef);
    }
    if (workflowDraft.type === "expense") {
      setExpenseForm((current) => ({
        ...current,
        userId: workflowDraft.userId,
        date: workflowDraft.date || defaultExpenseDate,
        purpose: workflowDraft.purpose || "",
        vehicleType: workflowDraft.vehicleType || current.vehicleType || "Bike",
        distanceKm: Number(workflowDraft.distanceKm ?? current.distanceKm ?? 0),
        ratePerKm: Number(workflowDraft.ratePerKm ?? current.ratePerKm ?? 8),
      }));
      focusForm(expenseFormRef);
    }
    onWorkflowDraftHandled?.(workflowDraft.id);
  }, [workflowDraft?.id, reps.map((rep) => rep.id).join("|"), defaultExpenseDate, canAssignTargets]);

  useEffect(() => {
    setPaymentForm((current) =>
      payableOutstanding.some((item) => item.id === current.outstandingId)
        ? current
        : { ...current, outstandingId: firstOutstanding },
    );
  }, [outstandingIds, firstOutstanding]);

  return (
    <>
      <Topbar title="Targets & Costs" subtitle="Phase 4: target assignment, travel costs, approval flow, and outstanding control." />
      <section className="content-grid">
        {canAssignTargets ? (
          <div ref={targetFormRef}>
            <Panel title="Assign Target">
              {!hasActiveSalesReps ? <div className="notice">No active sales reps are available in this reporting scope. Add or reactivate a sales rep before saving targets.</div> : null}
              <SimpleForm disabled={!hasActiveSalesReps} onSubmit={() => onCreate("/targets", targetForm)}>
	                <SelectField
	                  label="Sales executive"
	                  value={targetForm.userId}
	                  options={repOptions}
	                  disabled={!hasActiveSalesReps}
	                  actionLabel="Onboard employee"
	                  onAction={onAddEmployee}
	                  secondaryActionLabel="Open selected employee"
	                  secondaryActionDisabled={!targetForm.userId}
	                  onSecondaryAction={openEmployee ? () => openEmployee(targetForm.userId) : undefined}
	                  onChange={(userId) => setTargetForm({ ...targetForm, userId })}
	                />
                <TextField label="Product category" value={targetForm.productCategory} onChange={(productCategory) => setTargetForm({ ...targetForm, productCategory })} />
                <SelectField label="Period" value={targetForm.period} options={["Daily", "Weekly", "Monthly", "Quarterly"]} onChange={(period) => setTargetForm({ ...targetForm, period })} />
                <TextField label="Revenue target" type="number" value={targetForm.revenueTarget} onChange={(revenueTarget) => setTargetForm({ ...targetForm, revenueTarget })} />
                <TextField label="Quantity target" type="number" value={targetForm.quantityTarget} onChange={(quantityTarget) => setTargetForm({ ...targetForm, quantityTarget })} />
              </SimpleForm>
            </Panel>
          </div>
        ) : null}
        <div ref={expenseFormRef}>
          <Panel title="Submit Travel Expense">
            {!hasActiveSalesReps ? <div className="notice">No active sales reps are available in this reporting scope. Add or reactivate a sales rep before saving expenses.</div> : null}
            <SimpleForm disabled={!hasActiveSalesReps} onSubmit={() => onCreate("/expenses", expenseForm)}>
              <SelectField
                label="Sales executive"
                value={expenseForm.userId}
                options={repOptions}
                disabled={user.role === "sales" || !hasActiveSalesReps}
                actionLabel="Onboard employee"
                onAction={onAddEmployee}
                secondaryActionLabel="Open selected employee"
                secondaryActionDisabled={!expenseForm.userId}
                onSecondaryAction={openEmployee ? () => openEmployee(expenseForm.userId) : undefined}
                onChange={(userId) => setExpenseForm({ ...expenseForm, userId })}
              />
              <TextField label="Date" type="date" value={expenseForm.date} onChange={(date) => setExpenseForm({ ...expenseForm, date })} />
              <TextField label="Purpose" value={expenseForm.purpose} onChange={(purpose) => setExpenseForm({ ...expenseForm, purpose })} />
              <SelectField label="Vehicle" value={expenseForm.vehicleType} options={["Bike", "Car", "Public transport"]} onChange={(vehicleType) => setExpenseForm({ ...expenseForm, vehicleType })} />
              <TextField label="Distance KM" type="number" value={expenseForm.distanceKm} onChange={(distanceKm) => setExpenseForm({ ...expenseForm, distanceKm })} />
              <TextField label="Rate per KM" type="number" value={expenseForm.ratePerKm} onChange={(ratePerKm) => setExpenseForm({ ...expenseForm, ratePerKm })} />
            </SimpleForm>
          </Panel>
        </div>
      </section>
      <section className="content-grid spaced">
        <Panel title="Targets">
	          <ResourceTable
	            rows={resources.targets}
	            columns={[["userId", "Rep", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />], ["productCategory", "Category"], ["period", "Period"], ["revenueTarget", "Revenue", currency], ["quantityTarget", "Units"]]}
	            action={(row) => (
	              <div className="row-actions">
	                {canAssignTargets ? (
	                  <button className="mini-btn" type="button" onClick={() => startTargetFromRow(row)}>
	                    Assign Again
	                  </button>
	                ) : null}
	                <button className="mini-btn" type="button" onClick={() => startExpenseFromTarget(row)}>
	                  Add Expense
	                </button>
	              </div>
	            )}
	          />
        </Panel>
        <Panel title="Outstanding">
          {!hasOutstandingRecords ? <div className="notice">No outstanding records are available for this reporting scope. Record an invoice or order before posting a payment.</div> : null}
          {hasOutstandingRecords && !hasPayableOutstanding ? <div className="notice">All outstanding balances in this reporting scope are fully collected. No payment is due.</div> : null}
          {paymentExceedsBalance ? <div className="notice">Payment cannot exceed the remaining balance of {currency(paymentBalance)}.</div> : null}
          <div ref={paymentFormRef}>
            <SimpleForm disabled={!canPostPayment} onSubmit={() => onCreate(`/outstanding/${paymentForm.outstandingId}/payment`, { amount: paymentForm.amount })}>
            <SelectField
              label="Outstanding record"
              value={paymentForm.outstandingId}
              options={outstandingOptions}
              disabled={!hasPayableOutstanding}
              secondaryActionLabel="Open selected prospect"
              secondaryActionDisabled={!paymentOutstanding?.customerId}
              onSecondaryAction={openCustomer && paymentOutstanding?.customerId ? () => openCustomer(paymentOutstanding.customerId) : undefined}
              onChange={(outstandingId) => setPaymentForm({ ...paymentForm, outstandingId })}
            />
            <TextField label="Payment amount" type="number" min="0.01" max={paymentBalance || undefined} step="0.01" value={paymentForm.amount} onChange={(amount) => setPaymentForm({ ...paymentForm, amount })} />
            </SimpleForm>
          </div>
	          <ResourceTable
            rows={resources.outstanding}
            columns={[["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />], ["totalBilled", "Billed", currency], ["amountReceived", "Received", currency], ["outstandingAmount", "Due", currency], ["agingBucket", "Aging"], ["creditBlocked", "Credit Block"]]}
            action={(row) => Number(row.outstandingAmount || 0) > 0 ? (
              <div className="row-actions">
                <button className="mini-btn" type="button" onClick={() => startPaymentFromOutstanding(row)}>
                  Record Payment
                </button>
                {openMessage ? (
                  <button className="mini-btn" type="button" disabled={!canMessageOutstanding(row)} onClick={() => startPaymentReminder(row)}>
                    Send Reminder
                  </button>
                ) : null}
              </div>
            ) : null}
          />
        </Panel>
      </section>
      <section className="panel spaced">
        <h2>Expenses</h2>
        <ResourceTable
          rows={resources.expenses}
	          columns={[["userId", "Rep", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />], ["purpose", "Purpose"], ["distanceKm", "KM"], ["amount", "Amount", currency], ["status", "Status"]]}
          action={(row) => (
            <div className="row-actions">
              <button className="mini-btn" type="button" onClick={() => repeatExpense(row)}>
                Repeat Expense
              </button>
              {canApproveExpenses && row.status !== "Approved" ? (
                <AsyncActionButton
                  pendingLabel="Approving..."
                  errorMessage="We could not approve this expense. Please try again."
                  onAction={() => onApproveExpense(row.id)}
                >
                  Approve
                </AsyncActionButton>
              ) : null}
            </div>
          )}
        />
      </section>
    </>
  );

  function startPaymentFromOutstanding(row) {
    setPaymentForm({
      outstandingId: row.id,
      amount: Number(row.outstandingAmount || 0),
    });
    focusForm(paymentFormRef, "input");
  }

  function canMessageOutstanding(row) {
    return Boolean(customers.some((customer) => customer.id === row.customerId));
  }

  function startPaymentReminder(row) {
    const customer = customers.find((item) => item.id === row.customerId);
    if (!customer || !openMessage) return;
    openMessage(customer, paymentReminderDraft(row, customer));
  }

  function startTargetFromRow(row) {
    setTargetForm({
      userId: row.userId,
      productCategory: row.productCategory || "All",
      period: row.period || "Monthly",
      revenueTarget: Number(row.revenueTarget || 0),
      quantityTarget: Number(row.quantityTarget || 0),
    });
    focusForm(targetFormRef);
  }

  function startExpenseFromTarget(row) {
    setExpenseForm((current) => ({
      ...current,
      userId: row.userId,
      date: defaultExpenseDate,
      purpose: `${row.productCategory || "Sales"} target travel`,
    }));
    focusForm(expenseFormRef);
  }

  function repeatExpense(row) {
    setExpenseForm({
      userId: row.userId,
      date: defaultExpenseDate,
      purpose: row.purpose || "",
      vehicleType: row.vehicleType || "Bike",
      distanceKm: Number(row.distanceKm || 0),
      ratePerKm: Number(row.ratePerKm || 8),
    });
    focusForm(expenseFormRef);
  }

  function focusForm(ref, selector = "select, input") {
    window.setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      ref.current?.querySelector(selector)?.focus({ preventScroll: true });
    }, 0);
  }
}

function paymentReminderDraft(row, customer) {
  const dueAmount = currency(row.outstandingAmount || 0);
  const customerLabel = customer?.name || row.customerName || "your account";
  return {
    channel: "WhatsApp",
    template: "Follow-up",
    message: `Payment reminder for ${customerLabel}: ${dueAmount} is outstanding. Please share payment confirmation or expected payment date.`,
  };
}
